/**
 * pages/admin/AdminHomeDashboard.tsx
 *
 * Admin-only usage monitoring view. Rendered when userProfile.isAdmin is true.
 *
 * Layout (top → bottom):
 *   1. KPI cards with delta badges (Playfair display numbers)
 *   2. Per-user activity table (avatars, sort, columns incl. requests_sent)
 *
 * Filter options: Aujourd'hui (default), 7 jours, 30 jours, 90 jours.
 * "Aujourd'hui" = since midnight UTC.
 *
 * Updated May 2026: removed ActivityTimelineChart and Sparklines. Added 'today'
 * filter as default. Removed dailyTimeline prop from AdminKpiHeader.
 * Updated May 2026: Health Check accessible via sidebar icon (admin only).
 */

import { useState, useEffect } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { TabToggle } from '../../components/ui/TabToggle';
import { useAdminAnalytics, type DateRange } from '../../hooks/useAdminAnalytics';
import AdminKpiHeader from '../../components/admin/analytics/AdminKpiHeader';
import UsersActivityTable from '../../components/admin/analytics/UsersActivityTable';
import { RefreshCw } from 'lucide-react';

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: '7',     label: '7 jours' },
  { value: '30',    label: '30 jours' },
  { value: '90',    label: '90 jours' },
];

function useRelativeTime(date: Date): string {
  const [label, setLabel] = useState('');
  useEffect(() => {
    function compute() {
      const s = Math.floor((Date.now() - date.getTime()) / 1000);
      if (s < 10) return "À l'instant";
      if (s < 60) return `il y a ${s}s`;
      if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
      return `il y a ${Math.floor(s / 3600)} h`;
    }
    setLabel(compute());
    const id = setInterval(() => setLabel(compute()), 15_000);
    return () => clearInterval(id);
  }, [date]);
  return label;
}

export default function AdminHomeDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const { data, loading, error, refetch } = useAdminAnalytics(dateRange);
  const refreshLabel = useRelativeTime(data.lastRefreshedAt);

  function handleRangeChange(v: string) {
    if (v === 'today') { setDateRange('today'); return; }
    setDateRange(Number(v) as 7 | 30 | 90);
  }

  return (
    <AppLayout
      breadcrumbs={[{ label: 'Qollect', path: '/dashboard' }, { label: 'Monitoring' }]}
      actions={
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs text-neutral-400 hidden sm:inline">Mis à jour {refreshLabel}</span>
          <button
            onClick={refetch}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700 transition-colors disabled:opacity-40"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <TabToggle
            options={DATE_RANGE_OPTIONS}
            value={String(dateRange)}
            onChange={handleRangeChange}
          />
        </div>
      }
    >
      {error ? (
        <div className="rounded-2xl bg-error-50 border border-error-200 px-6 py-4 text-sm text-error-700">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <AdminKpiHeader
            activeUsers={data.activeUsers}
            requestsSent={data.requestsSent}
            requestsOpened={data.requestsOpened}
            requestsCompleted={data.requestsCompleted}
            clientsCreated={data.clientsCreated}
            deltas={data.deltas}
            previousValues={data.previousValues}
            loading={loading}
            dateRange={dateRange}
          />

          <UsersActivityTable
            users={data.users}
            loading={loading}
            dateRange={dateRange}
          />
        </div>
      )}
    </AppLayout>
  );
}
